import chroma from 'chroma-js'
import * as T from '@/types'
import observableWindow from '@/observable/window'

const THRESHOLD = 1.5

export function shouldTextBeLight (backgroundColor: string) {
    const whiteContrast = chroma.contrast('white', backgroundColor)
    return whiteContrast >= THRESHOLD
}

/**
 * @returns '#FFF' if it has enough contrast with @param backgroundColor.
 * Else, @returns '#333'.
 */
export function getBestTextColor (backgroundColor: string) {
    return shouldTextBeLight(backgroundColor) ? '#FFF' : '#333'
}

/**
 * @returns true if there is insufficient contrast between @param color and a white background.
 */
export function isBorderNeeded (color: string) {
    return chroma.contrast('white', color) < THRESHOLD
}

export function getColoredStyle (options: {
    primaryColor: T.Color,
    textColor?: T.Color,
    hovered?: boolean,
    border?: boolean,
    boxShadow?: boolean,
    opaque?: boolean,
}) {
    const { primaryColor, textColor, hovered, border, boxShadow } = options

    const backgroundColor = (() => {
        if (border || boxShadow) {
            if (hovered) return primaryColor
            return null
        }
        if (hovered) return chroma(primaryColor).darken(0.5).hex()
        return primaryColor
    })()

    const foregroundColor = (() => {
        if (textColor) return textColor
        if (border || boxShadow) {
            if (hovered) return getBestTextColor(primaryColor)
            else return primaryColor
        }
        return getBestTextColor(primaryColor)
    })()

    const borderValue = (() => {
        if (border) {
            return `calc(1rem * 2/32) solid ${primaryColor}`
        }
        else if (isBorderNeeded(primaryColor)) {
            return `calc(1rem * 1/32) solid rgba(0,0,0,0.08)`
        }
        return null
    })()

    const boxShadowValue = (() => {
        if (boxShadow) {
            const userAgent = window.navigator.userAgent
            if (userAgent.indexOf('MSIE') > -1
                || userAgent.indexOf('Trident/') > -1
                || userAgent.indexOf('Edge/') > -1){
                return `inset 0 0 0 2px ${primaryColor}`
            } else {
                return `inset 0 0 0 calc(1/16 * 1rem) ${primaryColor}`
            }
        }
        else if (isBorderNeeded(primaryColor)) {
            return `inset 0 0 0 calc(1/32 * 1rem) rgba(0,0,0,0.08)`
        }
        return null
    })()

    const style: T.Dictionary<any> = {}

    if (backgroundColor) style.backgroundColor = backgroundColor
    if (foregroundColor) style.color = foregroundColor
    if (borderValue) style.border = borderValue
    if (boxShadowValue) style.boxShadow = boxShadowValue

    return style
}

////////////////////////////////////////////////////////////////
// rem
////////////////////////////////////////////////////////////////

export function getPixelsAsRem (pixels: number) {
    const { currentUnitInPixels } = observableWindow
    return pixels/currentUnitInPixels
}

export function getPixelsAsRemCss (pixels: number) {
    const { defaultUnit } = observableWindow
    return `calc(1rem * ${pixels}/${defaultUnit})`
}

export function getRemAsPixels (rem: number) {
    const { currentUnitInPixels } = observableWindow
    return rem * currentUnitInPixels
}
